/**
 * Phase C Workspace Registry.
 * Future Church / Diocese / Parish / School / White Label / Enterprise =
 * add an entry here (+ optional page module), not a shell redesign.
 *
 * Nav rule: every item must open a real feature. Hide unimplemented items.
 */

import { openPlatformPortal } from "../Helper/studioCapabilities";
import { runtimeConfig } from "../../config/runtimeConfig";

const SECTION = (id, label, icon) => ({
  id,
  label,
  icon: icon || "dot",
  target: { type: "section", value: id },
});

const ROUTE = (id, label, href, icon) => ({
  id,
  label,
  icon: icon || "dot",
  target: { type: "route", value: href },
});

// /ops is gated by withStudioCapability(..., "ops"); hide the link when that gate would deny.
const OPS_ROUTE = (id, label, href, icon) =>
  Object.assign(ROUTE(id, label, href, icon), { requireCap: "can_access_ops" });

const HANDOFF = (id, label, icon) => ({
  id,
  label,
  icon: icon || "door",
  target: { type: "handoff", value: id },
});

export function sellerDashboardUrl(sellerType, sellerId, page) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "");
  const path = page || "home";
  return (
    base +
    "/seller-dashboard/" +
    path +
    "?seller_type=" +
    encodeURIComponent(sellerType) +
    "&seller_id=" +
    encodeURIComponent(sellerId)
  );
}

/**
 * Start Blade handoff for a workspace that declares bladeHandoff / autoHandoff.
 */
export function startWorkspaceHandoff(workspace) {
  if (!workspace || !workspace.bladeHandoff) {
    return Promise.resolve(false);
  }
  return Promise.resolve(
    openPlatformPortal({
      kind: "blade",
      requires_handoff: true,
      portal: workspace.bladeHandoff.portal,
      role: (workspace.requiredRoles && workspace.requiredRoles[0]) || null,
      path: workspace.bladeHandoff.path,
    })
  ).then(() => true);
}

/** @type {Record<string, object>} */
export const WORKSPACE_REGISTRY = {
  personal: {
    id: "personal",
    displayName: "Personal",
    icon: "user",
    route: "/workspace/personal",
    landingSection: "dashboard",
    entityType: null,
    requiredRoles: [],
    requiredCaps: [],
    featureFlags: [],
    bladeHandoff: null,
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: ["notifications", "account_links"],
    nav: [
      SECTION("dashboard", "Dashboard", "home"),
      ROUTE("continue", "Continue Watching", "/home", "play"),
      ROUTE("history", "Watch History", "/home", "clock"),
      ROUTE("favorites", "Favorites", "/home", "heart"),
      ROUTE("notifications", "Notifications", "/notification/view-all", "bell"),
      ROUTE("subscriptions", "Subscriptions", "/subscription", "card"),
      ROUTE("payment", "Payment History", "/payment-history", "receipt"),
      ROUTE("account", "Account", "/account", "user"),
    ],
  },
  creator: {
    id: "creator",
    displayName: "Creator Studio",
    icon: "creator",
    route: "/workspace/creator",
    landingSection: "dashboard",
    entityType: "creator",
    requiredRoles: ["creator"],
    requiredCaps: ["can_access_studio"],
    featureFlags: [],
    bladeHandoff: null,
    experience: { publicPath: "/creator/:slug" },
    theme: { accent: "#0f5c4c" },
    widgets: ["analytics", "recent_videos", "notifications"],
    nav: [
      SECTION("dashboard", "Dashboard", "home"),
      SECTION("profile", "Creator Page", "page"),
      SECTION("mission", "Biography", "flag"),
      SECTION("media", "Videos", "video"),
      OPS_ROUTE("livestreams", "Livestreams", "/ops", "live"),
      SECTION("community", "Community Posts", "chat"),
      SECTION("followers", "Followers", "users"),
      SECTION("storefront", "Storefront", "store"),
      SECTION("products", "Products", "box"),
      SECTION("donations", "Donations", "gift"),
      SECTION("analytics", "Analytics", "chart"),
      SECTION("settings", "Settings", "gear"),
    ],
  },
  organization: {
    id: "organization",
    displayName: "Organization",
    icon: "org",
    route: "/workspace/organization",
    landingSection: "dashboard",
    entityType: "organization",
    requiredRoles: ["organization"],
    requiredCaps: ["can_access_studio"],
    featureFlags: [],
    bladeHandoff: null,
    experience: { publicPath: "/organization/:slug" },
    theme: { accent: "#0f5c4c" },
    widgets: ["attention", "analytics", "recent_videos", "notifications", "team"],
    nav: [
      SECTION("dashboard", "Dashboard", "home"),
      SECTION("public", "Organization Page", "page"),
      SECTION("mission", "Profile & Mission", "flag"),
      SECTION("media", "Videos", "video"),
      OPS_ROUTE("livestreams", "Livestreams", "/ops", "live"),
      SECTION("community", "Community Posts", "chat"),
      SECTION("followers", "Followers", "users"),
      SECTION("storefront", "Storefront", "store"),
      SECTION("products", "Products", "box"),
      SECTION("donations", "Donations", "gift"),
      SECTION("analytics", "Analytics", "chart"),
      SECTION("team", "Organization Staff", "users"),
      SECTION("settings", "Settings", "gear"),
    ],
  },
  project: {
    id: "project",
    displayName: "Project Manager",
    icon: "project",
    route: "/workspace/project",
    landingSection: "dashboard",
    entityType: "project",
    requiredRoles: ["project_manager"],
    requiredCaps: ["can_access_studio"],
    featureFlags: [],
    bladeHandoff: null,
    experience: { publicPath: "/project/:slug" },
    theme: { accent: "#0f5c4c" },
    widgets: ["production", "analytics", "notifications"],
    nav: [
      SECTION("dashboard", "Dashboard", "home"),
      SECTION("profile", "Project Page", "page"),
      SECTION("mission", "Mission", "flag"),
      SECTION("media", "Media", "video"),
      OPS_ROUTE("livestreams", "Livestreams", "/ops", "live"),
      ROUTE("schedules", "Schedules", "/studio/timeline", "calendar"),
      SECTION("updates", "Community", "chat"),
      SECTION("funding", "Donations", "gift"),
      SECTION("analytics", "Analytics", "chart"),
      SECTION("settings", "Settings", "gear"),
    ],
  },
  sales: {
    id: "sales",
    displayName: "Sales Representative",
    icon: "sales",
    route: "/workspace/sales",
    landingSection: "dashboard",
    entityType: null,
    requiredRoles: ["sales_representative"],
    requiredCaps: [],
    featureFlags: [],
    autoHandoff: true,
    bladeHandoff: { portal: "sales_rep", path: "/sales-rep" },
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: [],
    nav: [
      HANDOFF("dashboard", "Dashboard", "home"),
      HANDOFF("pipeline", "Pipeline", "list"),
      HANDOFF("applications", "Applications", "check"),
      HANDOFF("organizations", "Organizations", "org"),
      HANDOFF("creators", "Creators", "creator"),
      HANDOFF("projects", "Projects", "project"),
      HANDOFF("coupons", "Coupons", "card"),
      HANDOFF("orders", "Orders", "box"),
      HANDOFF("commissions", "Commissions", "chart"),
      HANDOFF("payments", "Payments", "receipt"),
      HANDOFF("analytics", "Analytics", "chart"),
      HANDOFF("documents", "Documents", "page"),
    ],
  },
  moderator: {
    id: "moderator",
    displayName: "Moderator",
    icon: "shield",
    route: "/workspace/moderator",
    landingSection: "dashboard",
    entityType: null,
    requiredRoles: ["moderator"],
    requiredCaps: [],
    featureFlags: [],
    autoHandoff: true,
    bladeHandoff: { portal: "moderator", path: "/moderator" },
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: [],
    nav: [
      HANDOFF("queue", "Moderation Queue", "shield"),
      HANDOFF("content", "Reported Content", "flag"),
      HANDOFF("users", "Reported Users", "users"),
      HANDOFF("comments", "Comments", "chat"),
      HANDOFF("appeals", "Appeals", "check"),
      HANDOFF("audit", "Audit Log", "list"),
    ],
  },
  subadmin: {
    id: "subadmin",
    displayName: "SubAdmin",
    icon: "admin",
    route: "/workspace/subadmin",
    landingSection: "dashboard",
    entityType: null,
    requiredRoles: ["sub_admin", "administrator"],
    requiredCaps: ["is_platform_admin"],
    featureFlags: [],
    autoHandoff: true,
    bladeHandoff: { portal: "admin", path: "/admin" },
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: [],
    nav: [
      HANDOFF("dashboard", "Dashboard", "home"),
      HANDOFF("content", "Content", "video"),
      HANDOFF("creators", "Creators", "creator"),
      HANDOFF("organizations", "Organizations", "org"),
      HANDOFF("projects", "Projects", "project"),
      HANDOFF("users", "Users", "users"),
      HANDOFF("sales", "Sales", "sales"),
      HANDOFF("notifications", "Notifications", "bell"),
      HANDOFF("email", "Email", "mail"),
      HANDOFF("analytics", "Analytics", "chart"),
      HANDOFF("reports", "Reports", "chart"),
      HANDOFF("audit", "Audit", "list"),
    ],
  },
  advertiser: {
    id: "advertiser",
    displayName: "Advertiser",
    icon: "chart",
    route: "/workspace/advertiser",
    landingSection: "campaigns",
    entityType: null,
    requiredRoles: ["advertiser"],
    requiredCaps: [],
    featureFlags: [],
    bladeHandoff: null,
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: ["advertiser_campaigns", "advertiser_analytics"],
    // One view: My Campaigns renders both the campaign list and the Delivery summary.
    nav: [SECTION("campaigns", "My Campaigns", "list")],
  },
  partner: {
    id: "partner",
    displayName: "Partner",
    icon: "users",
    route: "/workspace/partner",
    landingSection: "commissions",
    entityType: null,
    requiredRoles: ["partner"],
    requiredCaps: [],
    featureFlags: [],
    bladeHandoff: null,
    experience: null,
    theme: { accent: "#0f5c4c" },
    widgets: ["analytics"],
    nav: [
      SECTION("commissions", "Commissions", "chart"),
      SECTION("settings", "Settings", "gear"),
    ],
  },
};

export const WORKSPACE_ORDER = [
  "personal",
  "creator",
  "organization",
  "project",
  "advertiser",
  "partner",
  "sales",
  "moderator",
  "subadmin",
];

export function getWorkspace(id) {
  return WORKSPACE_REGISTRY[id] || null;
}

export function listWorkspaces() {
  return WORKSPACE_ORDER.map((id) => WORKSPACE_REGISTRY[id]).filter(Boolean);
}

/**
 * Map portal key / role → workspace id.
 */
export function workspaceIdFromPortal(portal) {
  if (!portal) return null;
  const key = String(portal.key || portal.role || "").toLowerCase();
  if (key === "creator") return "creator";
  if (key === "organization") return "organization";
  if (key === "project_manager" || key === "project") return "project";
  if (key === "sales_representative" || key === "sales_rep") return "sales";
  if (key === "moderator") return "moderator";
  if (key === "advertiser") return "advertiser";
  if (key === "partner") return "partner";
  if (key === "sub_admin" || key === "administrator" || key === "admin") {
    return "subadmin";
  }
  return null;
}

/**
 * Whether caps allow this workspace in the switcher.
 */
export function isWorkspaceAvailable(workspace, caps) {
  if (!workspace) return false;
  // Ordinary accounts do not get a generic "Personal" workspace.
  if (workspace.id === "personal") return false;
  if (!caps || caps.error) return false;

  const portals = Array.isArray(caps.portals) ? caps.portals : [];
  const roles = Array.isArray(caps.platform_roles) ? caps.platform_roles : [];

  // is_platform_admin is also true for staff moderators; the server only lists the
  // administrator / sub_admin portal for real admins and sub-admins.
  if (workspace.id === "subadmin") {
    return (
      portals.some((p) =>
        ["administrator", "sub_admin"].includes(String(p.key || p.role))
      ) ||
      roles.some((r) =>
        ["administrator", "sub_admin", "admin"].includes(
          String(typeof r === "string" ? r : r.role || r.key || "")
        )
      )
    );
  }

  if (workspace.id === "advertiser" || workspace.id === "partner") {
    return (
      portals.some((p) => workspaceIdFromPortal(p) === workspace.id) ||
      roles.some((r) => {
        const key = String(typeof r === "string" ? r : r.role || r.key || "");
        return key === workspace.id;
      })
    );
  }

  if (workspace.bladeHandoff) {
    return portals.some((p) => workspaceIdFromPortal(p) === workspace.id);
  }

  if (workspace.entityType === "creator") {
    return (
      (caps.creator_ids || []).length > 0 ||
      portals.some((p) => workspaceIdFromPortal(p) === "creator")
    );
  }
  if (workspace.entityType === "organization") {
    return (
      (caps.organization_ids || []).length > 0 ||
      portals.some((p) => workspaceIdFromPortal(p) === "organization")
    );
  }
  if (workspace.entityType === "project") {
    return (
      (caps.project_ids || []).length > 0 ||
      portals.some((p) => workspaceIdFromPortal(p) === "project")
    );
  }

  return false;
}

export function availableWorkspaces(caps) {
  return listWorkspaces().filter((w) => isWorkspaceAvailable(w, caps));
}

/**
 * Filter nav items by caps / feature flags (Sprint 1: flags unused; caps for shared tools).
 */
export function resolveWorkspaceNav(workspace, caps) {
  if (!workspace || !Array.isArray(workspace.nav)) return [];
  return workspace.nav.filter((item) => {
    if (!item || !item.target) return false;
    if (item.requireCap && caps && !caps[item.requireCap]) return false;
    return true;
  });
}

export function buildBreadcrumbs(workspace, entityLabel, sectionId) {
  const crumbs = [];
  if (workspace) {
    crumbs.push({ label: workspace.displayName, to: workspace.route });
  }
  if (entityLabel) {
    crumbs.push({ label: entityLabel, to: null });
  }
  if (sectionId && workspace) {
    const item = (workspace.nav || []).find(
      (n) => n.id === sectionId || (n.target && n.target.value === sectionId)
    );
    if (item) {
      crumbs.push({ label: item.label, to: null });
    }
  }
  return crumbs;
}

export const STORAGE_KEY = "tpg_active_workspace";

export function readStoredWorkspace() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.workspaceId) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export function writeStoredWorkspace(workspaceId, entityId, entityLabel) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        workspaceId,
        entityId: entityId || null,
        entityLabel: entityLabel || null,
        updatedAt: Date.now(),
      })
    );
  } catch (e) {
    /* ignore quota */
  }
}
